import React, { createContext, useContext, useState, useEffect } from 'react';

// Design tokens for both light and dark modes
export const themes = {
  light: {
    name: 'light',
    // Backgrounds
    background: {
      primary: '#f5f5f5',
      secondary: '#ffffff',
      tertiary: '#e9e9e9',
      card: '#ffffff',
      elevated: '#ffffff',
      overlay: 'rgba(0, 0, 0, 0.5)',
    },
    // Text colors
    text: {
      primary: '#1a1a1a',
      secondary: '#666666',
      tertiary: '#888888',
      muted: '#999999',
      inverse: '#ffffff',
      link: '#3498db',
    },
    // Brand colors
    brand: {
      primary: '#e67e22',      // Orange
      primaryHover: '#d35400',
      secondary: '#3498db',    // Blue
      secondaryHover: '#2980b9',
      accent: '#27ae60',       // Green
      accentHover: '#219a52',
    },
    // UI element colors
    ui: {
      border: 'rgba(0, 0, 0, 0.1)',
      borderStrong: 'rgba(0, 0, 0, 0.2)',
      divider: 'rgba(0, 0, 0, 0.06)',
      shadow: 'rgba(0, 0, 0, 0.1)',
      shadowStrong: 'rgba(0, 0, 0, 0.2)',
    },
    // Button colors
    button: {
      primary: '#e67e22',
      primaryText: '#ffffff',
      primaryHover: '#d35400',
      secondary: '#6c757d',
      secondaryText: '#ffffff',
      secondaryHover: '#5a6268',
      success: '#27ae60',
      successText: '#ffffff',
      successHover: '#219a52',
      danger: '#dc3545',
      dangerText: '#ffffff',
      dangerHover: '#c82333',
      info: '#3498db',
      infoText: '#ffffff',
      infoHover: '#2980b9',
      ghost: 'transparent',
      ghostText: '#1a1a1a',
      ghostHover: 'rgba(0, 0, 0, 0.05)',
    },
    // Input colors
    input: {
      background: '#f8f8f8',
      border: 'rgba(0, 0, 0, 0.1)',
      borderFocus: '#e67e22',
      text: '#1a1a1a',
      placeholder: '#999999',
    },
    // Map marker colors
    markers: {
      tourVenue: '#27ae60',      // Green for tour venues
      embedCoords: '#3498db',    // Blue for Google Maps embed
      storedCoords: '#9b59b6',   // Purple for stored coords
      cluster: '#e67e22',        // Orange for clusters
      clusterMedium: '#d35400',
      clusterLarge: '#c0392b',
    },
    // Status colors
    status: {
      success: '#27ae60',
      warning: '#f1c40f',
      error: '#e74c3c',
      info: '#3498db',
    },
    // Header/Navigation
    header: {
      background: '#ffffff',
      text: '#1a1a1a',
      border: 'rgba(0, 0, 0, 0.1)',
    },
  },
  dark: {
    name: 'dark',
    // Backgrounds - darker and more contrasting
    background: {
      primary: '#121218',
      secondary: '#1c1c26',
      tertiary: '#2a2a36',
      card: '#1e1e28',
      elevated: '#2a2a36',
      overlay: 'rgba(0, 0, 0, 0.75)',
    },
    // Text colors
    text: {
      primary: '#f0f0f0',
      secondary: '#b0b0b0',
      tertiary: '#888888',
      muted: '#666666',
      inverse: '#1a1a1a',
      link: '#5dade2',
    },
    // Brand colors
    brand: {
      primary: '#89c6ac',
      primaryHover: '#f39c12',
      secondary: '#5dade2',
      secondaryHover: '#85c1e9',
      accent: '#2ecc71',
      accentHover: '#58d68d',
    },
    // UI element colors
    ui: {
      border: 'rgba(255, 255, 255, 0.1)',
      borderStrong: 'rgba(255, 255, 255, 0.2)',
      divider: 'rgba(255, 255, 255, 0.06)',
      shadow: 'rgba(0, 0, 0, 0.3)',
      shadowStrong: 'rgba(0, 0, 0, 0.5)',
    },
    // Button colors
    button: {
      primary: '#e67e22',
      primaryText: '#ffffff',
      primaryHover: '#f39c12',
      secondary: '#4a4a5a',
      secondaryText: '#ffffff',
      secondaryHover: '#5a5a6a',
      success: '#2ecc71',
      successText: '#ffffff',
      successHover: '#58d68d',
      danger: '#e74c3c',
      dangerText: '#ffffff',
      dangerHover: '#ec7063',
      info: '#5dade2',
      infoText: '#ffffff',
      infoHover: '#85c1e9',
      ghost: 'transparent',
      ghostText: '#f0f0f0',
      ghostHover: 'rgba(255, 255, 255, 0.1)',
    },
    // Input colors
    input: {
      background: '#252530',
      border: 'rgba(255, 255, 255, 0.1)',
      borderFocus: '#e67e22',
      text: '#f0f0f0',
      placeholder: '#666666',
    },
    // Map marker colors
    markers: {
      tourVenue: '#2ecc71',
      embedCoords: '#5dade2',
      storedCoords: '#bb8fce',
      cluster: '#89c6ac',
      clusterMedium: '#3f7f65',
      clusterLarge: '#1f4d3c',
    },
    // Status colors
    status: {
      success: '#2ecc71',
      warning: '#f4d03f',
      error: '#e74c3c',
      info: '#5dade2',
    },
    // Header/Navigation
    header: {
      background: '#1a1a24',
      text: '#f0f0f0',
      border: 'rgba(255, 255, 255, 0.1)',
    },
  },
};

// Create the context
const ThemeContext = createContext({
  theme: themes.light,
  isDarkMode: false,
  toggleTheme: () => {},
  setTheme: () => {},
});

// Theme provider component
export function ThemeProvider({ children }) {
  // Initialize from localStorage or system preference
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const themeKey = localStorage.getItem('vrc-theme');
      if (themeKey === 'dark') return true;
      if (themeKey === 'light') return false;
      // Default to light mode (no longer read legacy vrc-dark-mode)
      return false;
    }
    return false;
  });

  const theme = isDarkMode ? themes.dark : themes.light;

  // Persist preference (use vrc-theme so app defaults to light for everyone)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('vrc-theme', isDarkMode ? 'dark' : 'light');
      document.body.classList.toggle('dark-mode', isDarkMode);
      document.body.classList.toggle('light-mode', !isDarkMode);
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const setTheme = (mode) => {
    setIsDarkMode(mode === 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, isDarkMode, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Hook to use theme
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Utility function to get themed styles
export function getThemedStyles(theme, stylesFn) {
  return stylesFn(theme);
}

export default ThemeContext;
